// member-portal/src/components/Dashboard.js — FINAL: Fixed calendar bugs + Reset button
import React, { useState, useEffect } from 'react';
import fetchWithAuth from '../fetchWithAuth';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './Dashboard.css';
import NavButtons from './NavButtons';

const Dashboard = ({ navigateTo, deals, user }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [lifetimeStats, setLifetimeStats] = useState({ displayed: 0, views: 0, clicks: 0 });
  
  const [selectedMonth, setSelectedMonth] = useState(null); // Date object
  const [comparisonStats, setComparisonStats] = useState({ displayed: 0, views: 0, clicks: 0 });
  const [comparisonLabel, setComparisonLabel] = useState('Last Month');
  
  const [lastCategoryFetch, setLastCategoryFetch] = useState(0);
  const [showCalendar, setShowCalendar] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      const now = Date.now();
      if (now - lastCategoryFetch < 300000) return;

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/categories`);
        if (!response.ok) throw new Error('Failed to fetch categories');
        const data = await response.json();
        setCategories(data);
        setLastCategoryFetch(now);
      } catch (err) {
        console.error('Error fetching categories:', err);
        toast.error('Failed to fetch categories');
      }
    };

    const fetchMainStats = async () => {
      try {
        setStatsLoading(true);

        const response = await fetchWithAuth(
          `${process.env.REACT_APP_API_URL}/api/deals/deal-stats-monthly`,
          { headers: { 'Authorization': `Bearer ${user.token}` } },
          null,
          15000
        );

        if (response.ok) {
          const data = await response.json();
          setMonthlyStats(data);
          setComparisonStats(data.lastMonth || { displayed: 0, views: 0, clicks: 0 });
          setComparisonLabel(data.lastMonth?.label || 'Last Month');
        } else {
          throw new Error('Failed to load monthly stats');
        }
      } catch (err) {
        console.warn('Monthly stats failed, falling back to lifetime:', err);
        try {
          const legacyResponse = await fetchWithAuth(
            `${process.env.REACT_APP_API_URL}/api/members/deal-stats`,
            { headers: { 'Authorization': `Bearer ${user.token}` } }
          );
          if (legacyResponse.ok) {
            const data = await legacyResponse.json();
            setLifetimeStats(data);
            setComparisonStats(data);
            setComparisonLabel('Lifetime Totals');
          }
        } catch (e) {
          toast.error('Unable to load statistics');
        }
      } finally {
        setStatsLoading(false);
      }
    };

    if (user && user.token) {
      fetchCategories();
      fetchMainStats();
    }
    setLoading(false);
  }, [user]);

  // Update comparison when selectedMonth changes
  useEffect(() => {
    if (!monthlyStats) {
      setComparisonStats(lifetimeStats);
      setComparisonLabel('Lifetime Totals');
      return;
    }

    if (!selectedMonth) {
      setComparisonStats(monthlyStats.lastMonth || lifetimeStats);
      setComparisonLabel(monthlyStats.lastMonth?.label || 'Last Month');
      return;
    }

    const now = new Date();
    const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const selectedStr = `${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`;

    if (selectedStr === currentMonthStr) {
      setComparisonStats(monthlyStats.currentMonth || { displayed: 0, views: 0, clicks: 0 });
      setComparisonLabel(monthlyStats.currentMonth?.label || 'Current Month');
      return;
    }

    const fetchSelectedMonth = async () => {
      try {
        const response = await fetchWithAuth(
          `${process.env.REACT_APP_API_URL}/api/deals/deal-stats-monthly?month=${selectedStr}`,
          { headers: { 'Authorization': `Bearer ${user.token}` } }
        );

        if (response.ok) {
          const data = await response.json();
          setComparisonStats({
            displayed: data.displayed || 0,
            views: data.views || 0,
            clicks: data.clicks || 0
          });
          setComparisonLabel(data.label || selectedMonth.toLocaleString('default', { month: 'long', year: 'numeric' }));
        } else {
          setComparisonStats(monthlyStats.lastMonth || lifetimeStats);
          setComparisonLabel(monthlyStats.lastMonth?.label || 'Last Month');
        }
      } catch (err) {
        setComparisonStats(monthlyStats.lastMonth || lifetimeStats);
        setComparisonLabel(monthlyStats.lastMonth?.label || 'Last Month');
      }
    };

    fetchSelectedMonth();
  }, [selectedMonth, monthlyStats, user, lifetimeStats]);

  const totalDeals = deals.length;
  const activeDeals = deals.filter(deal => deal.active).length;
  const expiredDeals = deals.filter(deal => new Date(deal.endDate) < new Date()).length;

  const orgCategoryCounts = deals
    .filter(deal => deal.active)
    .flatMap(deal => deal.category.map(cat => cat._id.toString()))
    .reduce((acc, id) => ({ ...acc, [id]: (acc[id] || 0) + 1 }), {});

  const topOrgCategories = Object.entries(orgCategoryCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([id]) => categories.find(cat => cat._id.toString() === id)?.name || 'Unknown');

  const globalCategoryCounts = categories.reduce((acc, cat) => ({ ...acc, [cat._id]: cat.dealCount }), {});
  const topGlobalCategories = Object.entries(globalCategoryCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([id]) => categories.find(cat => cat._id.toString() === id)?.name || 'Unknown');

  const currentMonthStats = monthlyStats?.currentMonth || lifetimeStats;
  const ytdStats = monthlyStats?.ytd || lifetimeStats;

  const hasData = Object.values(currentMonthStats).some(v => v > 0) ||
                  Object.values(comparisonStats).some(v => v > 0) ||
                  Object.values(ytdStats).some(v => v > 0);

  const formatSelectedLabel = () => {
    if (!selectedMonth) return 'Last Month';
    return selectedMonth.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const resetComparison = () => {
    setSelectedMonth(null);
    setShowCalendar(false);
  };

  return (
    <div className="dashboard">
      <NavButtons navigateTo={navigateTo} user={user} activePage="dashboard" />
      
      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <>
          <div className="widget-tiles">
            <div className="widget-tile">
              <h3>Deals</h3>
              <p>Total Deals: {totalDeals}</p>
              <p>Active Deals: {activeDeals}</p>
              <p>Expired Deals: {expiredDeals}</p>
            </div>
            <div className="widget-tile">
              <h3>Categories</h3>
              <p><strong>Top 5 Tagged:</strong></p>
              <ul>{topOrgCategories.map((cat, idx) => <li key={idx}>{cat}</li>)}</ul>
              <p><strong>Top 5 Global:</strong></p>
              <ul>{topGlobalCategories.map((cat, idx) => <li key={idx}>{cat}</li>)}</ul>
            </div>
          </div>

          <div className="stats-section">
            <div className="stats-header">
              <h3>Deal Engagement Statistics</h3>
              <div className="month-picker-container">
                <span className="month-picker-label">Compare to:</span>
                <div className="relative flex items-center gap-3">
                  <button
                    onClick={() => setShowCalendar(!showCalendar)}
                    className="month-picker"
                  >
                    {formatSelectedLabel()}
                  </button>

                  {selectedMonth && (
                    <button
                      onClick={resetComparison}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all text-sm font-medium"
                    >
                      Reset
                    </button>
                  )}

                  {showCalendar && (
                    <div className="absolute top-full mt-2 z-50">
                      <DatePicker
                        selected={selectedMonth}
                        onChange={(date) => {
                          setSelectedMonth(date);
                          setShowCalendar(false);
                        }}
                        dateFormat="MM/yyyy"
                        showMonthYearPicker
                        inline
                        maxDate={new Date()}
                        calendarStartDay={1} // Monday start
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {statsLoading ? (
              <div className="loading-stats">Loading statistics...</div>
            ) : hasData ? (
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Current Month</th>
                    <th>{comparisonLabel}</th>
                    <th>YTD 2026</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="metric-cell">Total Displays</td>
                    <td className="numeric-cell">{currentMonthStats.displayed.toLocaleString()}</td>
                    <td className="numeric-cell">{comparisonStats.displayed.toLocaleString()}</td>
                    <td className="numeric-cell">{ytdStats.displayed.toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td className="metric-cell">Views</td>
                    <td className="numeric-cell">{currentMonthStats.views.toLocaleString()}</td>
                    <td className="numeric-cell">{comparisonStats.views.toLocaleString()}</td>
                    <td className="numeric-cell">{ytdStats.views.toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td className="metric-cell">Clicks</td>
                    <td className="numeric-cell">{currentMonthStats.clicks.toLocaleString()}</td>
                    <td className="numeric-cell">{comparisonStats.clicks.toLocaleString()}</td>
                    <td className="numeric-cell">{ytdStats.clicks.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <div className="no-stats">
                No engagement data yet for this period.<br />
                Statistics will appear as soon as your deals start getting impressions, views, or clicks.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;